import React, { useMemo, useCallback, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';

import { accountDiscoveryActions } from 'app-components/flux/account-discovery';
import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import { investmentQuotesV2Actions } from 'app-components/flux/investment-quotes-v2';
import {
  securitiesActions,
  securitiesSelectors,
  securitiesTypes,
  securitiesUtils,
} from 'app-components/flux/securities';
import * as tickerLogosActions from 'app-components/flux/ticker-logos/tickerLogosActions';
import * as tickerLogosSelectors from 'app-components/flux/ticker-logos/tickerLogosSelectors';
import { useFetchOnce, useLoadSetupStates } from 'app-components/hooks/utils';

import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import { openSecurityEditDialogAction } from 'components/Dialogs/SecurityEditDialog';
import Dump from 'components/Dump';
import LoadingLinearProgress from 'components/LoadingLinearProgress';
import QAmountField from 'components/QAmountField';
import QButton from 'components/QButton';
import QSkeleton from 'components/QSkeleton';
import SearchBox from 'components/SearchBox';
import TickerAvatar from 'components/TickerAvatar';
import ZeroStateView from 'components/ZeroStateView';
import SettingsHeader from 'containers/Settings/SharedComponents/SettingsHeader';

const ColumnIds = Object.freeze({
  LOGO: 'LOGO',
  SYMBOL: 'SYMBOL',
  NAME: 'NAME',
  TYPE: 'TYPE',
  PRICE: 'PRICE',
});

function Securities() {
  const dispatch = useDispatch();
  useFetchOnce(securitiesActions.getSecurities, securitiesSelectors, true);
  const [order, setOrder] = React.useState('asc');
  const [orderBy, setOrderBy] = React.useState(ColumnIds.NAME);
  const [globalSearch, setGlobalSearch] = useState(undefined);
  const searchMatch = globalSearch?.toLowerCase().trim();
  const quotePricesBySecurityId = useSelector((state) => securitiesSelectors.getQuotePricesBySecurityId(state));
  const securities = useSelector((state) => securitiesSelectors.getResourcesById(state));
  const symbolList = useSelector(securitiesSelectors.getSymbols);
  const { isUpdateRequired, isEmpty, isSkeleton, isReady, isLoading } = useLoadSetupStates(securitiesSelectors);

  useEffect(() => {
    dispatch(
      investmentQuotesV2Actions.getInvestmentQuotesV2(undefined, {
        axiosConfig: { params: { symbols: symbolList.join(',') } },
      }),
    );
  }, [dispatch, symbolList]);

  const stockLogo = useSelector((state) => featureFlagsSelectors.getFeatureFlag(state, 'stockLogo'));
  useFetchOnce(tickerLogosActions.getTickerLogos, tickerLogosSelectors, true, false, stockLogo);

  const columns = useMemo(
    () => [
      ...(stockLogo
        ? [
            {
              id: ColumnIds.LOGO,
              align: 'center',
              minWidth: 24,
              label: '',
            },
          ]
        : []),
      {
        id: ColumnIds.NAME,
        align: 'left',
        minWidth: 100,
        label: 'Name',
      },
      {
        id: ColumnIds.SYMBOL,
        align: 'left',
        minWidth: 110,
        label: 'Symbol',
      },
      {
        id: ColumnIds.TYPE,
        align: 'left',
        minWidth: 90,
        label: 'Type',
      },
      {
        id: ColumnIds.PRICE,
        align: 'right',
        minWidth: 100,
        label: 'Price',
      },
    ],
    [stockLogo],
  );

  const getCell = useCallback(
    (security, columnId) => {
      switch (columnId) {
        case ColumnIds.LOGO:
          return <TickerAvatar value={security.symbol} size='medium' />;
        case ColumnIds.SYMBOL:
          return security.symbol;
        case ColumnIds.NAME:
          return security.name;
        case ColumnIds.TYPE:
          return securitiesUtils.getSecurityTypeString(security.securityType);
        case ColumnIds.PRICE:
          return <QAmountField value={quotePricesBySecurityId.get(security.id)} expectedSign='positive' decimals={4} />;
        default:
          verify(false, `get cell: unhandled column id = ${columnId}`);
          return null;
      }
    },
    [quotePricesBySecurityId],
  );

  const rows = useMemo(
    () =>
      securities
        .filter(
          (value) =>
            !searchMatch ||
            value?.name.toLowerCase?.().includes(searchMatch) ||
            value?.symbol.toLowerCase?.().includes(searchMatch),
        )
        .toList()
        .sort((security1, security2) => {
          switch (orderBy) {
            case ColumnIds.LOGO:
            case ColumnIds.SYMBOL:
              return (security1.symbol?.localeCompare?.(security2.symbol) || 0) * (order === 'asc' ? 1 : -1);
            case ColumnIds.NAME:
              return (security1.name?.localeCompare?.(security2.name) || 0) * (order === 'asc' ? 1 : -1);
            case ColumnIds.TYPE:
              return (
                (security1.securityType?.localeCompare?.(security2.securityType) || 0) * (order === 'asc' ? 1 : -1)
              );
            case ColumnIds.PRICE:
              return (
                (quotePricesBySecurityId.get(security1.id) - quotePricesBySecurityId.get(security2.id)) *
                (order === 'asc' ? 1 : -1)
              );
            default:
              verify(false, `sort: unhandled column id = ${orderBy}`);
              return 0;
          }
        }),
    [securities, searchMatch, orderBy, order, quotePricesBySecurityId],
  );

  return (
    <>
      <SettingsHeader
        title='Securities'
        primaryItem={
          <QButton
            id='add-security'
            variant='add'
            onClick={() =>
              dispatch(
                openSecurityEditDialogAction({
                  clientId: uuidv4().toUpperCase(),
                  securityId: uuidv4().toUpperCase(),
                  securityIdType: 'CLIENT_ID',
                  securityType: securitiesTypes.SecurityTypeEnum.STOCK,
                }),
              )
            }
            style={{ letterSpacing: 1.25 }}
          >
            Add
          </QButton>
        }
        secondaryItem={
          <SearchBox
            placeholder={'Search Securities'}
            onSearch={(s) => setGlobalSearch(s)}
            initialValue={globalSearch}
            autoSearch
            autoFocus
            collapsable
          />
        }
      />

      {isLoading && <LoadingLinearProgress />}

      {isEmpty && (
        <Paper
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            height: '80vh',
          }}
        >
          <ZeroStateView
            animate={true}
            title='No securities to display'
            description='Set up investment account.'
            button='Add Account'
            qIllustration='boxEmptyDisabled'
            onClick={() =>
              dispatch(accountDiscoveryActions.doAccountDiscovery({ initiator: 'securities settings page' }))
            }
          />
        </Paper>
      )}
      {isUpdateRequired && (
        <Paper
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            height: '80vh',
          }}
        >
          <ZeroStateView
            title='Unable to fetch data'
            description="We can't fetch your data from the server. Please try again"
            qIllustration='boxEmptyDisabled'
            button
            onClick={() => dispatch(securitiesActions.getSecurities())}
          />
        </Paper>
      )}
      {isReady && (
        <Paper variant={'outlined'} sx={{ padding: 2 }}>
          <TableContainer sx={{ flexBasis: 'calc(100vh - 200px)' }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  {columns.map((column) => (
                    <TableCell key={column.id} align={column.align} style={{ minWidth: column.minWidth }}>
                      <TableSortLabel
                        active={orderBy === column.id}
                        direction={orderBy === column.id ? order : 'asc'}
                        onClick={() => {
                          setOrder(orderBy === column.id && order === 'asc' ? 'desc' : 'asc');
                          setOrderBy(column.id);
                        }}
                      >
                        {column.label}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((security) => (
                  <>
                    <Dump value={security} />
                    <TableRow
                      hover
                      role='checkbox'
                      tabIndex={-1}
                      key={security.id}
                      sx={{ position: 'relative' }}
                      onClick={() => {
                        dispatch(openSecurityEditDialogAction(security.toJS?.()));
                      }}
                      data-testid={security.id}
                    >
                      {columns.map((column) => (
                        <TableCell key={column.id} align={column.align}>
                          <QSkeleton show={isSkeleton}>{getCell(security, column.id)}</QSkeleton>
                        </TableCell>
                      ))}
                    </TableRow>
                  </>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </>
  );
}
export default Securities;
